import type { AppointmentStatus } from "./types";

/**
 * Single source of truth for appointment-status colors across the app.
 *
 * Used by the slotting board (calendar/week/month), guest-detail badges, the
 * slotting statistics panel, the request sidebar, and the dashboard slotting
 * card. Each status maps to ONE hue; every render form derives from it so
 * badges, calendar blocks and charts always agree.
 *
 * Hue mapping (do not diverge per-surface — matches the slotting board):
 *   TENTATIVE  → yellow  CONFIRMED → green  CHECKED_IN → blue
 *   CANCELLED  → grey
 */
export type AppointmentStatusColor = {
  /** Tailwind utility for a pastel chip background. */
  chipBg: string;
  /** Tailwind utility for chip foreground/text. */
  chipFg: string;
  /** Chip foreground as a raw CSS var — for inline borders on calendar blocks. */
  borderColor: string;
  /** Vivid semantic CSS var for chart swatches / dots / stacked bars. */
  swatch: string;
};

export const APPOINTMENT_STATUS_LIST: AppointmentStatus[] = [
  "TENTATIVE",
  "CONFIRMED",
  "CHECKED_IN",
  "CANCELLED",
];

export const APPOINTMENT_STATUS_COLORS: Record<AppointmentStatus, AppointmentStatusColor> = {
  TENTATIVE:  { chipBg: "chip-yellow-bg", chipFg: "chip-yellow-fg", borderColor: "var(--color-chip-yellow-foreground)", swatch: "var(--color-status-warning)" },
  CONFIRMED:  { chipBg: "chip-green-bg",  chipFg: "chip-green-fg",  borderColor: "var(--color-chip-green-foreground)",  swatch: "var(--color-status-success)" },
  CHECKED_IN: { chipBg: "chip-blue-bg",   chipFg: "chip-blue-fg",   borderColor: "var(--color-chip-blue-foreground)",   swatch: "var(--color-status-info)" },
  CANCELLED:  { chipBg: "chip-grey-bg",   chipFg: "chip-grey-fg",   borderColor: "var(--color-chip-grey-foreground)",   swatch: "var(--color-status-neutral)" },
};

/** Badge classes ("chip-X-bg chip-X-fg") for small status pills, derived from the canonical map. */
export const APPOINTMENT_STATUS_BADGE = Object.fromEntries(
  APPOINTMENT_STATUS_LIST.map((s) => [s, `${APPOINTMENT_STATUS_COLORS[s].chipBg} ${APPOINTMENT_STATUS_COLORS[s].chipFg}`]),
) as Record<AppointmentStatus, string>;

/** Calendar event-block styles: bg class + a border CSS var, derived from the canonical map. */
export const APPOINTMENT_STATUS_BLOCK = Object.fromEntries(
  APPOINTMENT_STATUS_LIST.map((s) => [
    s,
    {
      className: APPOINTMENT_STATUS_COLORS[s].chipBg,
      borderColor: APPOINTMENT_STATUS_COLORS[s].borderColor,
    },
  ]),
) as Record<AppointmentStatus, { className: string; borderColor: string }>;
